/**
 * The persistent heads-up display: brand wordmark, current phase + progress, the cut-off clock, and
 * the four performance meters. Sticky to the top; flex-wraps gracefully from phone to desktop.
 */
import { BookOpen } from "lucide-react";
import type { Dimension, Phase } from "@patc/content-schema";
import type { Scores } from "../game/scoring";
import { Clock } from "./Clock";
import { Meters } from "./Meters";
import { PHASE_LABEL } from "../lib/ui";
import logoRed from "../assets/brand/logo-borenius-red.svg";

export function Hud({
  phase,
  current,
  total,
  elapsedMs,
  limitMs,
  mode,
  dimensions,
  scores,
  onShowBackground,
  onExit,
}: {
  phase: Phase;
  current: number;
  total: number;
  elapsedMs: number;
  limitMs: number;
  mode: "timed" | "untimed";
  dimensions: Dimension[];
  scores: Scores;
  /** Opens the deal-background panel (always available during play). */
  onShowBackground?: () => void;
  /** When provided (untimed practice), shows an "Exit" control in the header. */
  onExit?: () => void;
}) {
  return (
    <header className="sticky top-0 z-20 border-b border-ink-150 bg-surface/95 backdrop-blur">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 sm:px-6">
        <img src={logoRed} alt="Borenius" className="h-5 w-auto" />
        <span className="hidden h-4 w-px bg-ink-200 sm:inline-block" aria-hidden="true" />
        <p className="text-sm text-ink-600">
          <span className="font-medium text-ink">{PHASE_LABEL[phase]}</span>
          <span className="text-ink-500"> · scenario {current} of {total}</span>
        </p>
        <div className="ml-auto flex items-center gap-3">
          {onShowBackground && (
            <button
              type="button"
              onClick={onShowBackground}
              className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-brand"
            >
              <BookOpen size={15} strokeWidth={2} aria-hidden="true" />
              Background
            </button>
          )}
          {onExit && (
            <button type="button" onClick={onExit} className="text-sm text-ink-500 hover:text-brand">
              Exit
            </button>
          )}
          <Clock elapsedMs={elapsedMs} limitMs={limitMs} mode={mode} />
        </div>
      </div>
      <div className="mx-auto max-w-5xl px-4 pb-3 sm:px-6">
        <Meters dimensions={dimensions} scores={scores} />
      </div>
    </header>
  );
}
